import { useAuth } from "@/hooks/useAuth";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { logger } from "@/lib/logger";
import { useLegalAcceptanceCheck } from "@/hooks/useLegalAcceptanceCheck";

type ProtectedRouteProps = {
  children: React.ReactNode;
};

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, profile, loading } = useAuth();
  const { t } = useTranslation();
  const { needsAcceptance, loading: legalLoading } = useLegalAcceptanceCheck();
  const path = typeof window !== "undefined" ? window.location.pathname : "";

  if (loading || legalLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <div className="ml-4 text-lg">{t("common.loading")}</div>
      </div>
    );
  }

  if (!user || !profile) {
    logger.debug("Redirecting to login - no user or profile");
    return <Navigate to="/login" replace />;
  }

  // Check if user needs to setup password (newly invited users)
  // Allow access to setup-password route, but block everything else
  const isOnSetupPasswordPage = window.location.pathname === "/setup-password";
  if (profile.requires_password_setup && !isOnSetupPasswordPage) {
    logger.debug("User requires password setup, redirecting to setup-password");
    return <Navigate to="/setup-password" replace />;
  }

  // Require acceptance of latest legal docs
  // Allow access to /terms page itself and /reset-password
  if (needsAcceptance && path !== "/terms" && path !== "/reset-password") {
    logger.debug("User needs legal acceptance, redirecting to /terms");
    return <Navigate to="/terms" replace />;
  }

  return <>{children}</>;
};
